import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Icon } from '@verantos-dev/shared-components';

// --- Shared metadata strip (OR-6288) ---------------------------------------
// The compact `✓ Concepts: N` row shown above every review tab's table, with an
// optional CSV download pinned to the right. Each tab supplies its own item set
// (Definition/Concepts use the count; Mapping uses found/not-found) so the row
// reads identically across tabs.

export interface MetaStripItem {
  key: string;
  icon: ReactNode;
  label: string;
  value: ReactNode;
}

export const ConceptGroupMetaStrip = ({
  items,
  onDownload,
  testIdPrefix,
  endContent,
}: {
  items: MetaStripItem[];
  onDownload?: () => void;
  testIdPrefix: string;
  /** Extra right-aligned content rendered just before the download icon
   *  (e.g. the Concepts tab's search field). */
  endContent?: ReactNode;
}) => {
  const { formatMessage } = useIntl();
  const downloadLabel = formatMessage({ id: 'epiChat.conceptGroupPreview.meta.downloadAria' });
  return (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', gap: 3, flexWrap: 'wrap' }}
      data-testid={`${testIdPrefix}-meta-strip`}
    >
      {items.map((item) => (
        <Stack key={item.key} direction="row" sx={{ alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Box sx={{ display: 'flex', color: 'text.secondary' }}>{item.icon}</Box>
          <Typography variant="body2" color="text.secondary" noWrap>
            {item.label}{' '}
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>
              {item.value}
            </Box>
          </Typography>
        </Stack>
      ))}
      {endContent || onDownload ? (
        <Stack
          direction="row"
          sx={{ marginLeft: 'auto', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}
        >
          <Box sx={{ flex: 1 }} />
          {endContent}
          {onDownload ? (
            <Tooltip title={downloadLabel}>
              <IconButton
                size="small"
                onClick={onDownload}
                aria-label={downloadLabel}
                sx={{ color: 'text.secondary', flexShrink: 0 }}
                data-testid={`${testIdPrefix}-meta-download`}
              >
                {/* Same VDS download glyph as the concepts grid (ConceptsExplorer). */}
                <Icon
                  id={`${testIdPrefix}-meta-download-icon`}
                  icon="download"
                  width={20}
                  height={20}
                  sx={{ fill: (theme) => theme.palette.text.secondary }}
                />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
};
